import AIcon from '@/components/A/AIcon/AIcon'
import styles from './ProfessorRatePanel.module.less'
import cls from 'classnames'
import AButton from '@/components/A/AButton/AButton'
import { Divider, Input, message, Radio } from 'antd'
import { CSSProperties, Fragment, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { commonUtils } from '@/helpers/commonUtils'
import { api } from '@/api'
import { ProfessorContext } from '@/Contexts/Contexts'
// import { useDebounceFn } from 'ahooks'

interface Props {
  project?: ProjectModels.ProjectDetail
  rateKey?: Models.ProfessorAnalyseModule
  onClose?: () => void
}

export const PROFESSOR_RATE_BTN_ID_PREFIX = 'rateBtnId'

function ProfessorRatePanel({ project, rateKey, onClose }: Props) {
  const { changeAlreadyRatedMap, professorRateInfo, changeProfessorRateInfo, unfinishedRateBtn } =
    useContext(ProfessorContext)
  const { t } = useTranslation()
  const [visible, setVisible] = useState(rateKey !== undefined)
  const map: { [key in Models.ProfessorAnalyseModule]: string } = {
    ad_eff_index: `1.${t('professor.adVideoScore')}`,
    subjective_response_curve: `2.${t('professor.subjectiveResponseCurve')}`,
    index_analyses: `3.${t('professor.indexAnalysis')}`,
    key_results: `4.${t('professor.keyResult')}`,
    the_golden_3s: `5.${t('professor.theGolden3Seconds')}`,
    // 专家评价调整，这里不展示下面 3 块，节省专家评测时间
    /*     key_message_moment: t('professor.informationTransmission'),
    brand_moment: t('professor.brandRelevance'),
    interest_moment: t('professor.interestStimulation'), */
    the_ending_3s: `6.${t('professor.theEnding3Seconds')}`,
    heat_map: `7.${t('professor.heatMap')}`,
  }
  const [rateList, setRateList] = useState<
    Array<{
      title: string
      value: number
      text: string
      parentKey: Models.ProfessorAnalyseModule
      markKey: '' | Models.ProfessorAnalyseResultModule
    }>
  >([])
  const handleClose = () => {
    onClose?.()
  }

  const handleSubmit = async () => {
    const analyseResult =
      professorRateInfo[commonUtils.underLineToCamelize(rateKey!) as keyof Models.RateMap]!
    if (!analyseResult) {
      message.warning(t('professor.message.rateUnfinished'))
      return
    }
    const allResultIsDone = analyseResult.every((res) => {
      return Boolean(res.analyseInfo.evaluate)
    })
    const resultsIsKeyResults = analyseResult.some((r) => r.module)
    const keyResultsIsDone = resultsIsKeyResults && allResultIsDone && analyseResult.length === 4
    const notKeyResultIsDone = !resultsIsKeyResults && allResultIsDone
    if (!(keyResultsIsDone || notKeyResultIsDone)) {
      message.warning(t('professor.message.rateUnfinished'))
      return
    }
    const body = {
      projectId: project!.id,
      analyseModule: rateKey!,
      analyseResult,
    }
    await api.expert.expertRate(body).send()
    if (unfinishedRateBtn.length <= 1) {
      message.success(t('professor.message.submitSuccess1'))
    } else {
      message.success(t('professor.message.submitSuccess'))
    }
    changeAlreadyRatedMap(commonUtils.underLineToCamelize(rateKey!) as keyof Models.RateMap)
  }

  useEffect(() => {
    setVisible(Boolean(rateKey) && Boolean(project))
  }, [rateKey, project])

  useEffect(() => {
    if (rateKey === undefined || professorRateInfo === undefined) {
      return
    }
    if (rateKey === 'key_results') {
      const results = professorRateInfo.keyResults
      setRateList([
        {
          title: t('professor.summary'),
          parentKey: 'key_results',
          markKey: 'summarize',
          value: results?.find((r) => r.module === 'summarize')?.analyseInfo.evaluate || 0,
          text: results?.find((r) => r.module === 'summarize')?.analyseInfo.suggestText || '',
        },
        {
          title: t('professor.advantages'),
          parentKey: 'key_results',
          markKey: 'advantage',
          value: results?.find((r) => r.module === 'advantage')?.analyseInfo.evaluate || 0,
          text: results?.find((r) => r.module === 'advantage')?.analyseInfo.suggestText || '',
        },
        {
          title: t('professor.disadvantages'),
          parentKey: 'key_results',
          markKey: 'disadvantage',
          value: results?.find((r) => r.module === 'disadvantage')?.analyseInfo.evaluate || 0,
          text: results?.find((r) => r.module === 'disadvantage')?.analyseInfo.suggestText || '',
        },
        {
          title: t('professor.suggestion1'),
          parentKey: 'key_results',
          markKey: 'suggestion',
          value: results?.find((r) => r.module === 'suggestion')?.analyseInfo.evaluate || 0,
          text: results?.find((r) => r.module === 'suggestion')?.analyseInfo.suggestText || '',
        },
      ])
    } else {
      const results =
        professorRateInfo[commonUtils.underLineToCamelize(rateKey!) as keyof Models.RateMap] || []
      setRateList([
        {
          title: '',
          parentKey: rateKey,
          markKey: '',
          value: results[0]?.analyseInfo.evaluate || 0,
          text: results[0]?.analyseInfo.suggestText || '',
        },
      ])
    }
  }, [rateKey, professorRateInfo])

  return visible && project ? (
    <div className={cls(styles.ratePanel, { [styles.visible]: visible })}>
      <div className={styles.ratePanelInner}>
        <div className={styles.header}>
          <div className={styles.title}>
            {map[rateKey!]} - {t('professor.expertReviews')}
          </div>
          <span style={{ cursor: 'pointer' }} onClick={handleClose} data-testid='professor-rate-panel-close-btn'>
            <AIcon type='icon-line-global_close1' />
          </span>
        </div>
        <div className={styles.content}>
          <div className={styles.rateContent}>
            {rateList.map((r, index) => {
              return (
                <Fragment key={index}>
                  {index > 0 && <Divider />}
                  <RateBox
                    {...r}
                    onChange={({ value, text }) => {
                      if (r.markKey === '') {
                        /*                         changeProfessorRateInfo((rateInfo) => {
                          const key = commonUtils.underLineToCamelize(
                            rateKey!
                          ) as keyof Models.RateMap
                          rateInfo[key] = [
                            {
                              module: undefined,
                              analyseInfo: {
                                evaluate: value as 1 | 2,
                                suggestText: text,
                              },
                            },
                          ]
                          return rateInfo
                        }) */
                        changeProfessorRateInfo({
                          ...professorRateInfo,
                          [commonUtils.underLineToCamelize(rateKey!)]: [
                            {
                              module: undefined,
                              analyseInfo: {
                                evaluate: value,
                                suggestText: text,
                              },
                            },
                          ],
                        })
                      } else {
                        const rKey = commonUtils.underLineToCamelize(
                          r.parentKey!
                        ) as keyof Models.RateMap
                        const before = professorRateInfo[rKey] || []
                        let after = before
                        const targetIndex = after.findIndex((m) => m.module === r.markKey)
                        if (targetIndex === -1) {
                          after = [
                            ...after,
                            {
                              module: r.markKey,
                              analyseInfo: { evaluate: value as 1 | 2, suggestText: text },
                            },
                          ]
                        } else {
                          after.splice(targetIndex, 1, {
                            module: r.markKey,
                            analyseInfo: { evaluate: value as 1 | 2, suggestText: text },
                          })
                        }
                        changeProfessorRateInfo({
                          ...professorRateInfo,
                          [rKey]: after,
                        })
                      }
                    }}
                  />
                </Fragment>
              )
            })}
          </div>
          <AButton testId='professor-rate-panel-submit-btn' className={styles.submitBtn} type='primary' onClick={handleSubmit}>
            {unfinishedRateBtn.length > 1 ? t('professor.next') : t('professor.submit')}
          </AButton>
        </div>
      </div>
    </div>
  ) : (
    <div className={styles.ratePanel}></div>
  )
}

function RateBox(props: {
  title: string
  value: number
  text: string
  onChange: (p: { value: number; text: string }) => void
}) {
  const { t } = useTranslation()
  const [text, setText] = useState<string>(props.text)
  const [value, setValue] = useState<number>(props.value)
  const options = [
    {
      value: 1,
      label: t('professor.option1'),
    },
    {
      value: 2,
      label: t('professor.option2'),
    },
  ]

  const title = props.title ? props.title + '-' : ''
  /*   const emitChange = () => {
    props.onChange({ value, text })
  } */
  // const { run } = useDebounceFn(emitChange, { wait: 0 })

  const handleBlur = () => {
    props.onChange({ value, text })
  }

  // useEffect(() => {
  // run()
  //
  // console.log('22222', value, text)
  // props.onChange({ value, text })
  // }, [value])

  useEffect(() => {
    setValue(props.value)
    setText(props.text)
  }, [props.value, props.text])

  return (
    <div className={styles.rateBox}>
      <p className={cls(styles.title, styles.required)}>
        {title}
        {t('professor.resultJudgment')}
      </p>
      <Radio.Group
        style={{ display: 'flex', flexDirection: 'column', rowGap: 8 }}
        defaultValue={props.value}
        value={value}
        onChange={(e) => {
          setValue(e.target.value)
          props.onChange({ value: e.target.value, text })
        }}
        options={options}
      ></Radio.Group>
      <p className={styles.title}>{t('professor.suggestion')}</p>
      <Input.TextArea
        className={styles.suggestion}
        defaultValue={props.text}
        value={text}
        placeholder={t('professor.placeholder')}
        onKeyDown={(e) => {
          e.stopPropagation()
        }}
        onChange={(e) => {
          setText(e.target.value)
        }}
        onBlur={handleBlur}
      />
    </div>
  )
}

export function RateBtn(props: {
  onClick: () => void
  style?: CSSProperties
  rateKey: Models.ProfessorAnalyseModule
}) {
  const { alreadyRatedMap } = useContext(ProfessorContext)
  const { t } = useTranslation()
  const handleClick = () => {
    props.onClick()
  }
  const done =
    alreadyRatedMap[commonUtils.underLineToCamelize(props.rateKey) as keyof Models.RateMap]

  return (
    <button
      className={cls(styles.rateBtn, { [styles.done]: done })}
      onClick={handleClick}
      style={props.style}
    >
      {t('professor.expertReviews')}
    </button>
  )
}

export default ProfessorRatePanel
